import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { Error as ErrorIcon } from "@mui/icons-material";
import { motion } from "framer-motion";

interface DetailErrorStateProps {
  onBack: () => void;
  message?: string;
}

/**
 * Common error state for detail screens
 * - Shows when item not found or API error
 * - Provides back navigation button
 */
export const DetailErrorState: React.FC<DetailErrorStateProps> = ({
  onBack,
  message = "Unable to load details",
}) => {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(180deg, #f5f7fa 0%, #ffffff 100%)",
        px: 2,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box
          sx={{
            textAlign: "center",
            maxWidth: 400,
          }}
        >
          {/* Error Icon */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 80,
              height: 80,
              borderRadius: "50%",
              bgcolor: "error.light",
              mb: 3,
            }}
          >
            <ErrorIcon
              sx={{
                fontSize: 48,
                color: "error.main",
              }}
            />
          </Box>

          {/* Error Message */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              mb: 1,
              color: "text.primary",
            }}
          >
            Oops! Something went wrong
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              mb: 4,
            }}
          >
            {message}. Please try again later.
          </Typography>

          {/* Back Button */}
          <Button
            variant="contained"
            size="large"
            onClick={onBack}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              background: "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
              boxShadow: "0 4px 16px rgba(26, 35, 126, 0.2)",
              "&:hover": {
                background: "linear-gradient(135deg, #3949ab 0%, #1a237e 100%)",
                transform: "translateY(-2px)",
                boxShadow: "0 8px 24px rgba(26, 35, 126, 0.3)",
              },
              transition: "all 0.2s",
            }}
          >
            Go Back
          </Button>
        </Box>
      </motion.div>
    </Box>
  );
};
